import { useState, useRef, useCallback, useEffect } from 'react';

const PIP_WIDTH = 76;
const PIP_HEIGHT = 100;
const BOUNDS_MARGIN = 8;
const DRAG_THRESHOLD = 8;

/**
 * [F구역 ⑧-3] 단체 화상 작은 창(PIP) 드래그 및 탭 제어 훅
 * - 모바일 터치 및 데스크톱 마우스 포인터 이벤트 통합 처리
 * - 8px 이동 감지 전까지는 탭으로 판정하여 onTap 콜백 호출 (주인공 교체)
 * - 8px 초과 이동 시 드래그로 전환, requestAnimationFrame + translate3d 로 부드러운 이동
 * - 드래그 종료 시 컨테이너 울타리(경계) 내로 좌표를 제한(clamped)하여 고정
 * - 창 크기 변경(resize) 시 또는 외부 요청 시 위치 초기화 지원
 */
export function useGroupPipDrag({ containerRef, onTap }) {
  // id별 저장된 위치: { [id]: { left: number, top: number } }
  const [positions, setPositions] = useState({});

  // 드래그 진행 상태 ref
  const dragRef = useRef({
    active: false,
    id: null,
    pointerId: null,
    startX: 0,
    startY: 0,
    currentDx: 0,
    currentDy: 0,
    isDragging: false,
    baseLeft: 0,
    baseTop: 0,
    el: null,
  });

  const rafIdRef = useRef(null);
  const justDraggedRef = useRef(false);
  const dragTimeoutRef = useRef(null);

  // 울타리 계산 (컨테이너 내부 경계)
  const getBounds = useCallback(() => {
    const container = containerRef?.current;
    const containerRect = container
      ? container.getBoundingClientRect()
      : { width: window.innerWidth, height: window.innerHeight };

    const minLeft = BOUNDS_MARGIN;
    const maxLeft = Math.max(minLeft, containerRect.width - PIP_WIDTH - BOUNDS_MARGIN);
    const minTop = BOUNDS_MARGIN;
    const maxTop = Math.max(minTop, containerRect.height - PIP_HEIGHT - BOUNDS_MARGIN);

    return { minLeft, maxLeft, minTop, maxTop };
  }, [containerRef]);

  const handlePointerDown = useCallback((id, e) => {
    if (!e.isPrimary || e.button !== 0) return;
    const el = e.currentTarget;
    if (!el) return;

    try {
      el.setPointerCapture(e.pointerId);
    } catch {}

    const containerRect = containerRef?.current
      ? containerRef.current.getBoundingClientRect()
      : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };

    const elRect = el.getBoundingClientRect();
    const baseLeft = elRect.left - containerRect.left;
    const baseTop = elRect.top - containerRect.top;

    dragRef.current = {
      active: true,
      id,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      currentDx: 0,
      currentDy: 0,
      isDragging: false,
      baseLeft,
      baseTop,
      el,
    };
  }, [containerRef]);

  const handlePointerMove = useCallback((e) => {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;

    if (!dragRef.current.isDragging) {
      if (Math.hypot(dx, dy) <= DRAG_THRESHOLD) return;
      dragRef.current.isDragging = true;
      if (dragRef.current.el) {
        dragRef.current.el.style.transition = 'none';
      }
    }

    dragRef.current.currentDx = dx;
    dragRef.current.currentDy = dy;

    if (!rafIdRef.current) {
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        if (dragRef.current.el && dragRef.current.isDragging) {
          dragRef.current.el.style.transform = `translate3d(${dragRef.current.currentDx}px, ${dragRef.current.currentDy}px, 0)`;
        }
      });
    }
  }, []);

  const finishDrag = useCallback((isCancel = false) => {
    if (!dragRef.current.active) return;

    const { id, isDragging, pointerId, baseLeft, baseTop, currentDx, currentDy, el } = dragRef.current;

    dragRef.current.active = false;
    dragRef.current.isDragging = false;

    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    try {
      if (pointerId != null && el?.hasPointerCapture?.(pointerId)) {
        el.releasePointerCapture(pointerId);
      }
    } catch {}

    if (el) {
      el.style.transform = '';
      el.style.transition = '';
    }

    if (!isDragging) {
      if (!isCancel && id) {
        onTap?.(id);
      }
      return;
    }

    justDraggedRef.current = true;
    if (dragTimeoutRef.current) clearTimeout(dragTimeoutRef.current);
    dragTimeoutRef.current = setTimeout(() => {
      justDraggedRef.current = false;
      dragTimeoutRef.current = null;
    }, 250);

    if (!isCancel && id) {
      const targetLeft = baseLeft + currentDx;
      const targetTop = baseTop + currentDy;
      const { minLeft, maxLeft, minTop, maxTop } = getBounds();
      const clampedX = Math.round(Math.max(minLeft, Math.min(maxLeft, targetLeft)));
      const clampedY = Math.round(Math.max(minTop, Math.min(maxTop, targetTop)));

      setPositions((prev) => ({
        ...prev,
        [id]: { left: clampedX, top: clampedY },
      }));
    }
  }, [getBounds, onTap]);

  const handlePointerUp = useCallback(() => {
    finishDrag(false);
  }, [finishDrag]);

  const handlePointerCancel = useCallback(() => {
    finishDrag(true);
  }, [finishDrag]);

  const handleClickCapture = useCallback((e) => {
    if (justDraggedRef.current) {
      e.stopPropagation();
      e.preventDefault();
      justDraggedRef.current = false;
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setPositions({});
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      if (dragTimeoutRef.current) clearTimeout(dragTimeoutRef.current);
    };
  }, []);

  const resetPositions = useCallback(() => {
    setPositions({});
  }, []);

  const getPipHandlers = useCallback((id) => ({
    onPointerDown: (e) => handlePointerDown(id, e),
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
    onClickCapture: handleClickCapture,
  }), [handlePointerDown, handlePointerMove, handlePointerUp, handlePointerCancel, handleClickCapture]);

  return {
    positions,
    resetPositions,
    getPipHandlers,
  };
}
